"use client";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { SectionTemplate } from "components/individuals/blocks";
import { FaCheckCircle } from "react-icons/fa";
import { FeaturesListItemProps } from "./features-list-section";
import { EnterpriseCarousel } from "components/enterprise-carousel";
import { Button, Button_T } from "@ui/components";
import { enterpriseColors } from "@areahub/theme/src/foundations/colors/enterprise";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { customRichText } from "components/individuals/mdx-components";
import { RichTextContent } from "@graphcms/rich-text-types";
import { colors } from "@areahub/theme";

export interface IndustriesBusinessContinuitySectionProps {
  title: string;
  subtitle: string;

  /**
   * Subtitle content
   */
  content: {
    text: string;
  };

  titleColor: string;
  subtitleColor: string;
  bg: string;

  /**
   * List of risks information displayed under Key Risks
   */
  featuresList: {
    title: string;
    featuresListItems: FeaturesListItemProps[];
  };
  /**
   * Content for the carousel
   */
  highlightsSection: {
    highlight: {
      description: string;
      citation: string;
    }[];
  };

  ctaSection: {
    title: string;
    buttons: Button_T[];
    content: {
      raw: RichTextContent;
    };
  };
}

export const IndustriesBusinessContinuitySection = ({
  title,
  subtitle,
  content,
  titleColor,
  subtitleColor,
  bg,
  featuresList,
  highlightsSection,
  ctaSection,
}: IndustriesBusinessContinuitySectionProps) => {
  const captionFontSize = {
    fontSize: { base: "14px", md: "16px", lg: "17px", xl: "18px" },
  };
  const subtitleFontSize = {
    fontSize: {
      base: "16px",
      md: "18px",
      lg: "19px",
      xl: "20px",
    },
  };

  return (
    <SectionTemplate id="business-continuity-section" bg={bg} borderRadius={0}>
      <Heading
        as={"h2"}
        fontWeight={"bold"}
        fontSize={{
          base: 26,
          md: 34,
          lg: 38,
          xl: 45,
        }}
        lineHeight={1.2}
        textColor={titleColor}
      >
        {title}
      </Heading>
      <Heading
        as={"h3"}
        {...subtitleFontSize}
        mt={{
          base: 3,
          lg: 6,
        }}
        textColor={subtitleColor}
        lineHeight={1.2}
      >
        {subtitle}
      </Heading>
      <Text {...captionFontSize} my={{ base: 3, lg: 6 }}>
        {content?.text}
      </Text>

      <Heading
        as={"h3"}
        mt={{
          base: 6,
          lg: 10,
        }}
        textColor={"#88aed4"}
        fontSize={{
          base: 24,
          md: 28,
          lg: 29,
          xl: 30,
        }}
      >
        {featuresList?.title}
      </Heading>
      <Grid
        mt={{
          base: 3,
          lg: 6,
        }}
        templateColumns={{
          lg: "repeat(2, 1fr)",
        }}
        gap={5}
      >
        {featuresList?.featuresListItems.map((listItem, idx) => (
          <>
            {listItem?.featuresListSubitem &&
            listItem?.featuresListSubitem?.length > 0 ? (
              // Key Risk Nested List Item
              <GridItem
                key={`key-risks-nested-list-item-${idx}`}
                colSpan={{
                  lg: 2,
                }}
              >
                <Flex flexDir={"row"} gap={4}>
                  <Box>
                    <FaCheckCircle fontSize={26} fill={subtitleColor} />
                  </Box>
                  <Box>
                    <Heading as={"h4"} {...subtitleFontSize} lineHeight={"1.5"}>
                      {listItem?.title}
                    </Heading>
                    <Grid
                      templateColumns={{
                        lg: "repeat(2, 1fr)",
                      }}
                      gap={{
                        lg: 7,
                      }}
                    >
                      {listItem?.featuresListSubitem.map(
                        (listSubItem, listItemIdx) => (
                          <GridItem
                            key={`key-risks-nested-list-item-${idx}-${listItemIdx}`}
                            colSpan={{
                              lg: 2,
                            }}
                          >
                            <Heading
                              as={"h5"}
                              {...captionFontSize}
                              textColor="#6e8cab"
                              lineHeight={"1.5"}
                              mt={3}
                            >
                              {listSubItem?.title}
                            </Heading>
                            {listSubItem?.description && (
                              <Text {...captionFontSize}>
                                {listSubItem?.description}
                              </Text>
                            )}
                            <UnorderedList>
                              <Grid
                                templateColumns={{
                                  lg: "repeat(2, 1fr)",
                                }}
                                gap={{
                                  lg: 10,
                                }}
                              >
                                {listSubItem?.featuresListSubitem?.map(
                                  (item, listSubItemIdx) => (
                                    <GridItem
                                      key={`key-risks-nested-list-item-${idx}-${listItemIdx}-${listSubItemIdx}`}
                                    >
                                      <ListItem>
                                        <Heading as={"h6"} {...captionFontSize}>
                                          {item?.title}
                                        </Heading>
                                        <Text {...captionFontSize}>
                                          {item?.description}
                                        </Text>
                                      </ListItem>
                                    </GridItem>
                                  ),
                                )}
                              </Grid>
                            </UnorderedList>
                          </GridItem>
                        ),
                      )}
                    </Grid>
                  </Box>
                </Flex>
              </GridItem>
            ) : (
              // Key Risk List Item
              <GridItem key={`key-risks-list-item-${idx}`}>
                <Flex flexDir={"row"} gap={4}>
                  <Box mt={1}>
                    <FaCheckCircle fontSize={26} fill={subtitleColor} />
                  </Box>
                  <Box>
                    <Heading as={"h4"} {...subtitleFontSize} lineHeight={"1.5"}>
                      {listItem?.title}
                    </Heading>
                    <Text {...captionFontSize} mt={3}>
                      {listItem?.description}
                    </Text>
                  </Box>
                </Flex>
              </GridItem>
            )}
          </>
        ))}
      </Grid>
      <Flex
        mt={{
          lg: 16,
        }}
        flexDir={{
          base: "column",
          lg: "row",
        }}
      >
        {/* Key Information Carousel */}
        <Flex justify={"center"} w={{ lg: "50%" }}>
          <EnterpriseCarousel
            slideContent={highlightsSection.highlight}
            nonActiveIndicatorColor="white"
          />
        </Flex>
        {/* CTA Section */}
        <Box
          w={{ lg: "50%" }}
          mt={{
            base: 5,
            lg: 0,
          }}
        >
          <Heading
            as={"h2"}
            fontSize={{
              base: 24,
              md: 28,
              lg: 29,
              xl: 30,
            }}
            mb={2}
          >
            {ctaSection?.title}
          </Heading>
          <RichText
            content={ctaSection?.content?.raw}
            renderers={customRichText("", 0, 3)}
          />
          <Flex justify={"center"} mt={5}>
            <Button
              bg={enterpriseColors["blue"]["200"]}
              color={"white"}
              py={{
                lg: 6,
              }}
              textTransform={"uppercase"}
              {...ctaSection?.buttons[0]}
            />
          </Flex>
        </Box>
      </Flex>
    </SectionTemplate>
  );
};
